import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  createProduct,
  updateProduct,
  fetchProductByUuid,
  validateProductCode,
  ProductFormData,
} from "@/modules/productStorefront/features/ProductManagement/api/productManagementApi";

interface CreateUpdateProductState {
  loadingCreate: boolean;
  createError: string | null;
  createdProduct: any | null;
  loadingGet: boolean;
  getError: string | null;
  fetchedProduct: any | null;
  loadingUpdate: boolean;
  updateError: string | null;
  updatedProduct: any | null;
  codeValidationLoading: boolean;
  codeValidationError: string | null;
  codeIsAvailable: boolean | null;
}

const initialState: CreateUpdateProductState = {
  loadingCreate: false,
  createError: null,
  createdProduct: null,
  loadingGet: false,
  getError: null,
  fetchedProduct: null,
  loadingUpdate: false,
  updateError: null,
  updatedProduct: null,
  codeValidationLoading: false,
  codeValidationError: null,
  codeIsAvailable: null,
};

const createUpdateProductSlice = createSlice({
  name: "createUpdateProduct",
  initialState,
  reducers: {
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },
    setCreatedProduct(state, action: PayloadAction<any | null>) {
      state.createdProduct = action.payload;
    },
    setLoadingGet(state, action: PayloadAction<boolean>) {
      state.loadingGet = action.payload;
    },
    setGetError(state, action: PayloadAction<string | null>) {
      state.getError = action.payload;
    },
    setFetchedProduct(state, action: PayloadAction<any | null>) {
      state.fetchedProduct = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },
    setUpdatedProduct(state, action: PayloadAction<any | null>) {
      state.updatedProduct = action.payload;
    },
    setCodeValidationLoading(state, action: PayloadAction<boolean>) {
      state.codeValidationLoading = action.payload;
    },
    setCodeValidationError(state, action: PayloadAction<string | null>) {
      state.codeValidationError = action.payload;
    },
    setCodeIsAvailable(state, action: PayloadAction<boolean | null>) {
      state.codeIsAvailable = action.payload;
    },
    resetCreateUpdateProductState(state) {
      state.loadingCreate = false;
      state.createError = null;
      state.createdProduct = null;

      state.loadingGet = false;
      state.getError = null;
      state.fetchedProduct = null;

      state.loadingUpdate = false;
      state.updateError = null;
      state.updatedProduct = null;

      state.codeValidationLoading = false;
      state.codeValidationError = null;
      state.codeIsAvailable = null;
    },
  },
});

export const {
  setLoadingCreate,
  setCreateError,
  setCreatedProduct,

  setLoadingGet,
  setGetError,
  setFetchedProduct,

  setLoadingUpdate,
  setUpdateError,
  setUpdatedProduct,

  setCodeValidationLoading,
  setCodeValidationError,
  setCodeIsAvailable,

  resetCreateUpdateProductState,
} = createUpdateProductSlice.actions;

export default createUpdateProductSlice.reducer;

export const createProductAction =
  (
    requestData: ProductFormData,
    onSuccess?: (data: any) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createProduct(requestData);
      dispatch(setCreatedProduct(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create the product.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const getProductAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingGet(true));
    dispatch(setGetError(null));
    try {
      const response = await fetchProductByUuid(uuid);
      dispatch(setFetchedProduct(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch the product.";
      dispatch(setGetError(message));
    } finally {
      dispatch(setLoadingGet(false));
    }
  };

export const updateProductAction =
  (
    uuid: string,
    requestData: ProductFormData,
    onSuccess?: (data: any) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateProduct(uuid, requestData);
      dispatch(setUpdatedProduct(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update the product.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const validateProductCodeAction =
  (code: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setCodeValidationLoading(true));
    dispatch(setCodeValidationError(null));
    dispatch(setCodeIsAvailable(null));
    try {
      const response = await validateProductCode(code);
      dispatch(setCodeIsAvailable(response.data?.isAvailable ?? false));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to validate product code.";
      dispatch(setCodeValidationError(message));
    } finally {
      dispatch(setCodeValidationLoading(false));
    }
  };
